/**
 * B428 (h)-(j) — the ask bar's ANSWER PAGE, the blessed frames
 * (apps/design #answerpages, Pathik 2026-09-09: "looks good") as ONE
 * shared component. Anatomy, exactly the frames': the "Ask anything ·
 * <archetype>" eyebrow, the operator's question verbatim in quotes, the
 * answer SENTENCE, the shape-led body (the caller composes stat cards /
 * chart / table from the answer's DATA SHAPE — presentation is part of
 * the answer, not a fixed template), SUGGESTED FOLLOW-UPS as chips, a
 * persistent ask field so the conversation continues in place, and the
 * "How this was answered" footnote naming the read.
 *
 * In the package (not hand-rolled in an app) per the 2026-07-26 UI law:
 * the admin's real page and the design gallery's exemplars render THIS,
 * so the blessed spec and the shipped screen cannot drift apart.
 */
import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Chip, GlassCard, Input } from "./components.js";

/** One big-figure card of a Figure answer (the frames' stat anatomy). */
export function AnswerStat(props: {
  readonly label: string;
  readonly figure: string;
  readonly note?: string;
}): ReactNode {
  return (
    <GlassCard label={props.label}>
      <p className="t-answerstat-figure">{props.figure}</p>
      {props.note !== undefined ? <p className="t-answerstat-note">{props.note}</p> : null}
    </GlassCard>
  );
}

export interface AnswerFollowUp {
  readonly label: string;
  readonly onPick: () => void;
}

/** The frames' follow-up block: chips + the persistent ask field. */
function AnswerPageFollowUps(props: {
  readonly followUps: readonly AnswerFollowUp[];
  readonly onAsk?: ((text: string) => void) | undefined;
  readonly askId: string;
}): ReactNode {
  const [text, setText] = useState("");
  return (
    <div className="t-answerpage-followups">
      {props.followUps.length > 0 ? (
        <>
          <span className="t-answerpage-followups-title">Suggested follow-ups</span>
          <div className="t-answerpage-chips">
            {props.followUps.map((f) => (
              <Chip key={f.label} label={f.label} pressed={false} onPress={f.onPick} />
            ))}
          </div>
        </>
      ) : null}
      {props.onAsk !== undefined ? (
        <form
          className="t-answerpage-ask"
          onSubmit={(e) => {
            e.preventDefault();
            const q = text.trim();
            if (q === "") return;
            props.onAsk?.(q);
            setText("");
          }}
        >
          <Input
            id={props.askId}
            label="Ask a follow-up"
            placeholder="Ask further — the answer opens on this page"
            value={text}
            onChange={setText}
          />
        </form>
      ) : null}
    </div>
  );
}

/**
 * The full answer-page canvas. `children` is the shape-led body the
 * caller composed from the answer's data (stat-card grid, chart, table
 * — the blessed frames' three archetypes); `busy` marks a body still
 * being read, so a page is honest while the wire answers.
 */
export function AnswerPage(props: {
  /** The frames' eyebrow tail — "Figure answer" / "Series answer" / "Rows answer". */
  readonly archetype: string;
  /** The operator's question, verbatim — the page quotes it. */
  readonly question: string;
  /** The answer in one operator sentence (the numbers live in the body). */
  readonly sentence: string;
  /** "How this was answered — …": the named read behind the numbers. */
  readonly how: string;
  readonly children?: ReactNode;
  readonly followUps?: readonly AnswerFollowUp[];
  /** Present = the persistent ask field renders and submits here. */
  readonly onAsk?: ((text: string) => void) | undefined;
  /** Distinguishes ask fields when two pages ever coexist (tests, gallery). */
  readonly askId?: string;
  readonly busy?: boolean;
}): ReactNode {
  return (
    <article className="t-answerpage" aria-busy={props.busy === true ? true : undefined}>
      <div>
        <span className="t-answerpage-eyebrow">{`Ask anything · ${props.archetype}`}</span>
        <h1 className="t-answerpage-q">{`“${props.question}”`}</h1>
        <p className="t-answerpage-sentence">{props.sentence}</p>
      </div>
      {props.children}
      <AnswerPageFollowUps
        followUps={props.followUps ?? []}
        onAsk={props.onAsk}
        askId={props.askId ?? "answer-followup-ask"}
      />
      <p className="t-footnote">{props.how}</p>
    </article>
  );
}

/* ------------------------------------------------------------------ B460
   (a) — the CONVERSATION: /ask no longer replaces the answer on every
   ask. Each settled ask is one AnswerExchange block, appended in order
   inside an AnswerThreadPage whose composer stays pinned at the foot.

   B864 (Pathik 2026-10-16/17, the frames 723:2 / 723:157 / 724:2 / 726:2,
   approved "looks great"): the exchange is TWO MESSAGE ROWS — the
   operator's line beside their avatar, the assistant's beside its spark —
   in an 880 column; the question is said plainly (no eyebrow, no quotes),
   the provenance folds under "How this was answered", and the composer is
   a card pinned to the viewport's foot over a fade, with the latest
   follow-ups as cards above it. In the package per the 2026-07-26 UI law:
   the admin's thread and the gallery's exemplar render THIS. */

/** Who is asking — the initials the operator's avatar wears (the thread provides it). */
const ThreadWhoContext = createContext<string>("You");

/** The pinned ask form — one mint for the page and the thread. */
export function AskField(props: {
  readonly onAsk: (text: string) => void;
  readonly askId: string;
  readonly label?: string;
  readonly placeholder?: string;
  readonly busy?: boolean;
}): ReactNode {
  const [text, setText] = useState("");
  const send = (): void => {
    const q = text.trim();
    if (q === "") return;
    props.onAsk(q);
    setText("");
  };
  return (
    <form
      className="t-answerpage-ask t-composer"
      onSubmit={(e) => {
        e.preventDefault();
        send();
      }}
    >
      <label className="t-visually-hidden" htmlFor={props.askId}>
        {props.label ?? "Ask a follow-up"}
      </label>
      <input
        id={props.askId}
        className="t-composer-field"
        type="text"
        autoComplete="off"
        placeholder={props.placeholder ?? "Ask anything"}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <div className="t-composer-bar">
        <button type="submit" className="t-composer-send" aria-label={props.busy === true ? "The assistant is working" : "Send"} aria-busy={props.busy === true ? true : undefined} data-empty={text.trim() === "" ? "true" : undefined}>
          <span aria-hidden="true">{props.busy === true ? "■" : "→"}</span>
        </button>
      </div>
    </form>
  );
}

/** One message row: an avatar beside a body column (the frames' 32 / 24 / column). */
function MessageRow(props: { readonly who: "you" | "assistant"; readonly children: ReactNode }): ReactNode {
  const initials = useContext(ThreadWhoContext);
  return (
    <div className={`t-msg t-msg--${props.who}`}>
      <span className={`t-msg-avatar t-msg-avatar--${props.who}`} aria-hidden="true">
        {props.who === "you" ? initials : "✦"}
      </span>
      <div className="t-msg-body">{props.children}</div>
    </div>
  );
}

/**
 * One question-and-answer block of the conversation: the operator's row,
 * then the assistant's (sentence, the shape-led body, its still-pressable
 * follow-up chips, the provenance folded). Earlier exchanges stay fully
 * readable.
 */
/**
 * B864 (723:2, turn 2) — a form's reply ("Promotion name: Summer sale\nKind:
 * Percent off …") is said back as CHIPS, one per field, not as a run-on
 * line. Anything else the operator typed stays a plain question.
 */
export function formReplyOf(question: string): readonly { readonly field: string; readonly value: string }[] | undefined {
  if (!question.includes("\n")) return undefined;
  const pairs = question
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) => {
      const m = /^([^:]{1,60}):\s+(.+)$/.exec(line);
      return m === null ? undefined : { field: m[1]!.trim(), value: m[2]!.trim() };
    });
  if (pairs.length < 2 || pairs.some((p) => p === undefined)) return undefined;
  return pairs as { readonly field: string; readonly value: string }[];
}

export function AnswerExchange(props: {
  readonly archetype: string;
  readonly question: string;
  readonly sentence: string;
  readonly how: string;
  readonly children?: ReactNode;
  readonly followUps?: readonly AnswerFollowUp[];
  /** What the ask was made on — the page, the thing on screen (B852 e) — shown as chips under the question. */
  readonly context?: readonly string[];
  readonly busy?: boolean;
}): ReactNode {
  const followUps = props.followUps ?? [];
  const said = formReplyOf(props.question);
  return (
    <section className="t-answerexchange" data-archetype={props.archetype} aria-busy={props.busy === true ? true : undefined}>
      <MessageRow who="you">
        {said === undefined ? (
          <h2 className="t-answerpage-q t-answerexchange-q">{props.question}</h2>
        ) : (
          <h2 className="t-answerpage-q t-answerexchange-q t-answerexchange-q--said" aria-label={props.question}>
            {said.map((pair) => (
              <span key={pair.field} className="t-saidchip">
                <span className="t-saidchip-field">{pair.field}</span>
                {pair.value}
              </span>
            ))}
          </h2>
        )}
        {props.context !== undefined && props.context.length > 0 ? (
          <div className="t-msg-context">
            {props.context.map((c) => (
              <span key={c} className="t-contextchip">
                <span className="t-contextchip-dot" aria-hidden="true" />
                {c}
              </span>
            ))}
          </div>
        ) : null}
      </MessageRow>
      <MessageRow who="assistant">
        {props.sentence !== "" ? <p className="t-answerpage-sentence">{props.sentence}</p> : null}
        {props.children}
        {followUps.length > 0 ? (
          <div className="t-answerpage-followups t-answerexchange-followups">
            <span className="t-answerpage-followups-title">Suggested follow-ups</span>
            <div className="t-answerpage-chips">
              {followUps.map((f) => (
                <Chip key={f.label} label={f.label} pressed={false} onPress={f.onPick} />
              ))}
            </div>
          </div>
        ) : null}
        {props.how !== "" ? (
          <details className="t-answer-how">
            <summary className="t-answer-how-fold">How this was answered</summary>
            <p className="t-footnote">{props.how}</p>
          </details>
        ) : null}
      </MessageRow>
    </section>
  );
}

/** A follow-up offered as a card above the composer (the frames' DRILL DOWN / DO NEXT pair). */
export interface AnswerFollowUpCard extends AnswerFollowUp {
  readonly kicker: string;
}

/**
 * The conversation canvas: exchanges appended in order (children), the
 * composer pinned to the foot with the latest follow-ups as cards above
 * it. `door` renders beside the eyebrow — the history door and its kin. A
 * read-only replay simply passes no onAsk and provides its own way back.
 */
export function AnswerThreadPage(props: {
  /** The page eyebrow tail — "Conversation" live, "Stored conversation" in replay. */
  readonly archetype?: string;
  readonly door?: ReactNode;
  readonly children: ReactNode;
  readonly onAsk?: ((text: string) => void) | undefined;
  readonly askId?: string;
  readonly askLabel?: string;
  readonly busy?: boolean;
  /** The operator's initials for their avatar ("PS"); "You" when the shell has no name. */
  readonly who?: string;
  /** The latest answer's follow-ups, offered as cards above the composer. */
  readonly followUpCards?: readonly AnswerFollowUpCard[];
  /** B864 — the session rail beside the thread (open); absent = the thread alone. */
  readonly rail?: ReactNode;
}): ReactNode {
  const cards = props.followUpCards ?? [];
  // B864 — the newest exchange comes into view when it appears (the dock is
  // fixed at the foot, so the thread itself has to move): the first paint stays put.
  const itemsRef = useRef<HTMLDivElement | null>(null);
  const newestRef = useRef<Element | null | undefined>(undefined);
  useEffect(() => {
    const items = itemsRef.current;
    if (items === null) return;
    const all = items.querySelectorAll(".t-answerexchange");
    const newest = all.length === 0 ? null : all[all.length - 1] ?? null;
    const first = newestRef.current === undefined;
    if (!first && newest !== null && newest !== newestRef.current && typeof newest.scrollIntoView === "function") {
      newest.scrollIntoView({ block: "start", behavior: "smooth" });
    }
    newestRef.current = newest;
  });
  const article = (
      <article className="t-answerthread" aria-busy={props.busy === true ? true : undefined}>
        <div className="t-answerthread-head">
          <span className="t-answerpage-eyebrow">{`Ask anything · ${props.archetype ?? "Conversation"}`}</span>
          {props.door}
        </div>
        <div className="t-answerthread-items" ref={itemsRef}>{props.children}</div>
        {props.onAsk !== undefined ? (
          <div className="t-answerthread-ask t-answerthread-dock">
            {cards.length > 0 ? (
              <div className="t-followcards">
                {cards.map((c) => (
                  <button key={c.label} type="button" className="t-followcard" onClick={c.onPick}>
                    <span className="t-followcard-kicker">{c.kicker}</span>
                    <span className="t-followcard-q">{c.label}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <AskField onAsk={props.onAsk} askId={props.askId ?? "answer-thread-ask"} {...(props.askLabel !== undefined ? { label: props.askLabel } : {})} {...(props.busy === true ? { busy: true } : {})} />
          </div>
        ) : null}
      </article>
  );
  return (
    <ThreadWhoContext.Provider value={props.who ?? "You"}>
      {props.rail !== undefined ? (
        <div className="t-answerthread-layout">
          {article}
          {props.rail}
        </div>
      ) : (
        article
      )}
    </ThreadWhoContext.Provider>
  );
}
